import { TableFilterConfig, TableFilterOption } from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import {
  DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES,
  DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
} from '@/lib/constants/pagination';
import { getShortClients } from '@/modules/clients/api/clients.api';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import {
  getOverheadStatusOptions,
  getOverheadTypeOptions,
} from '@/modules/common/components/molecules/badge/status-configs/overhead-status-config';
import { getShortProjects } from '@/modules/projects';
import { renderProjectFilterLabelWithAvatar } from '@/modules/projects/helpers/project-dropdown-option';

import { OverheadStatus, OverheadType } from '../types/overhead';
import {
  OVERHEADS_FILTER_KEY_CLIENT,
  OVERHEADS_FILTER_KEY_PROJECT,
  OVERHEADS_FILTER_KEY_SEARCH,
  OVERHEADS_FILTER_KEY_STATUS,
  OVERHEADS_FILTER_KEY_TYPE,
  OVERHEADS_SEARCH_FILTER_MIN_CHARACTERS,
} from './overheads-filter-keys';

// NOTE: the period date range is rendered as a standalone DateRangePickerV2
// next to the filters (see overheads-page.tsx), not as a dropdown filter.
export const getOverheadsFilterConfig = (): TableFilterConfig[] => [
  {
    type: 'search',
    name: OVERHEADS_FILTER_KEY_SEARCH,
    label: i18n.t('common:search'),
    minCharacters: OVERHEADS_SEARCH_FILTER_MIN_CHARACTERS,
    placeholder: i18n.t('authenticated.overheads:searchPlaceholder'),
  },
  {
    type: 'autocomplete',
    name: OVERHEADS_FILTER_KEY_TYPE,
    label: i18n.t('authenticated.overheads:filters.type'),
    displayDot: true,
    options: getOverheadTypeOptions([OverheadType.Income, OverheadType.Cost]),
  },
  {
    type: 'autocomplete',
    name: OVERHEADS_FILTER_KEY_STATUS,
    label: i18n.t('authenticated.overheads:filters.status'),
    displayDot: true,
    options: getOverheadStatusOptions([
      OverheadStatus.Pending,
      OverheadStatus.Invoiced,
      OverheadStatus.Planned,
      OverheadStatus.Paid,
    ]),
  },
  {
    type: 'dynamic-autocomplete',
    name: OVERHEADS_FILTER_KEY_PROJECT,
    label: i18n.t('authenticated.overheads:filters.project'),
    withSearch: true,
    loadOptions: async params =>
      getShortProjects({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
      }),
    renderLabel: renderProjectFilterLabelWithAvatar,
  },
  {
    type: 'dynamic-autocomplete',
    name: OVERHEADS_FILTER_KEY_CLIENT,
    label: i18n.t('authenticated.overheads:filters.client'),
    withSearch: true,
    loadOptions: async params =>
      getShortClients({
        filter: { searchString: params.searchString },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES },
      }),
    renderLabel: (value: string, option: TableFilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar name={value} image={option.avatar} size="xs" shape="rounded" />
        <span className="text-sm">{value}</span>
      </div>
    ),
  },
];
